import React from 'react';
import { createPortal } from 'react-dom';
import { Camp, CampDate } from '../../utils/types';
import { useCampBooking } from '../../hooks/useCampBooking';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import { useCampContext } from '../../context/CampContext';
import { useAddCampToSchedule, useRemoveCampFromSchedule } from '../../api';
import CampDescription from './shared/CampDescription';
import CampKeyInfo from './shared/CampKeyInfo';
import CampDatesSection from './shared/CampDatesSection';
import CampAdditionalInfo from './shared/CampAdditionalInfo';
import CampOrganizerSection from './shared/CampOrganizerSection';
import CampMap from './CampMap';
import SimilarCamps from './SimilarCamps';
import NewsletterSignupModal from '../NewsletterSignupModal';
import LoginModal from '../login/LoginModal';
import { LoginModalProvider } from '../../context/LoginModalContext';
import './CampContent.css';

interface CampContentProps {
  camp: Camp;
  variant: 'page' | 'modal';
  scheduledDateIds?: string[];
  onSimilarCampClick?: (camp: Camp) => void;
  newsletterSignupEnabled?: boolean;
  analyticsPrefix?: string;
}

const CampContent: React.FC<CampContentProps> = ({
  camp,
  variant,
  scheduledDateIds: externalScheduledDateIds,
  onSimilarCampClick,
  newsletterSignupEnabled = false,
  analyticsPrefix,
}) => {
  const { isLoggedIn } = useUserContext();
  const { showSuccess, showError } = useNotifications();
  const { setSchedule, currentScheduleUuid, schedule } = useCampContext();
  const addCampToSchedule = useAddCampToSchedule();
  const removeCampFromSchedule = useRemoveCampFromSchedule();

  const prefix = analyticsPrefix || (variant === 'page' ? 'camp_page' : 'camp_details');

  const {
    showNewsletterModal,
    showRegistrationModal,
    isInstantBooking,
    handleBookDate,
    handleNewsletterSignup,
    handleSkipNewsletter,
    handleNewsletterModalClose,
    handleRegister,
    handleLogin,
    setShowRegistrationModal,
  } = useCampBooking({
    camp,
    analyticsPrefix: prefix,
    newsletterEnabled: newsletterSignupEnabled,
  });

  const getCampImage = (): string => {
    return camp.images.find(image => image.type === 'default')?.url || '/images/camp-card-default-image.png';
  };

  const getScheduledDateIds = (): string[] => {
    if (externalScheduledDateIds) return externalScheduledDateIds;
    if (!schedule) return [];
    return schedule.scheduled_camps
      .filter((sc: { camp: Camp }) => sc.camp.id === camp.id)
      .map((sc: { camp_date: CampDate }) => String(sc.camp_date.id));
  };

  const handleScheduleToggle = (campDateId: string) => {
    if (!isLoggedIn) {
      setShowRegistrationModal(true);
      return;
    }
    if (!currentScheduleUuid) {
      showError('Something went wrong. Please try again later.', new Error('No schedule selected'));
      return;
    }

    const isScheduled = getScheduledDateIds().includes(String(campDateId));

    if (isScheduled) {
      removeCampFromSchedule.mutate(
        { scheduleUuid: currentScheduleUuid, campId: camp.id, campDateId },
        {
          onSuccess: (data) => { setSchedule(data); showSuccess('Camp removed from schedule'); },
          onError: (err) => { showError('Failed to remove camp from schedule. Please try again.', err); }
        }
      );
    } else {
      addCampToSchedule.mutate(
        { scheduleUuid: currentScheduleUuid, campId: camp.id, campDateId },
        {
          onSuccess: (data) => { setSchedule(data); showSuccess('Camp added to schedule'); },
          onError: (err) => { showError('Failed to add camp to schedule. Please try again.', err); }
        }
      );
    }
  };

  return (
    <div className={`camp-content camp-content--${variant}`}>
      <h1 className="camp-content-title">{camp.name}</h1>

      <div className="camp-content-layout">
        <div className="camp-content-info">
          <CampDescription description={camp.description} />
          <CampKeyInfo camp={camp} />
        </div>

        <div className="camp-content-media">
          <div className="camp-content-image">
            <img
              src={getCampImage()}
              alt={camp.name}
              className="camp-content-image-inner"
            />
          </div>
          <div className="camp-content-map">
            <CampMap
              mapPoints={camp.latitude && camp.longitude ? [{
                latitude: camp.latitude,
                longitude: camp.longitude,
                organizers: camp.organizer ? [{
                  id: camp.organizer.id,
                  name: camp.organizer.name,
                  instant_booking: camp.organizer.instant_booking
                }] : []
              }] : undefined}
            />
          </div>
        </div>
      </div>

      <CampDatesSection
        camp={camp}
        scheduledDateIds={getScheduledDateIds()}
        isInstantBooking={isInstantBooking}
        onBookDate={handleBookDate}
        onScheduleToggle={handleScheduleToggle}
      />

      {camp.additional_info && (
        <CampAdditionalInfo additionalInfo={camp.additional_info} />
      )}

      {camp.organizer && (
        <CampOrganizerSection organizer={camp.organizer} />
      )}

      {camp.organizer?.id && (
        <SimilarCamps
          filters={{ organizer_id: camp.organizer.id }}
          currentCamp={camp}
          title={`Other Camps by ${camp.organizer.name}`}
          displayDates={true}
          onCampClick={onSimilarCampClick || (() => {})}
        />
      )}

      {camp.activity_keywords?.length > 0 && (
        <SimilarCamps
          filters={{ activity_keywords: camp.activity_keywords }}
          currentCamp={camp}
          title="Similar Camps by Other Providers"
          displayDates={false}
          displayOrganizer={true}
          onCampClick={onSimilarCampClick || (() => {})}
        />
      )}

      {showRegistrationModal && createPortal(
        <LoginModalProvider initialStep="register">
          <LoginModal
            onClose={() => setShowRegistrationModal(false)}
            onLogin={handleLogin}
            onRegister={handleRegister}
          />
        </LoginModalProvider>,
        document.body
      )}

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={handleNewsletterModalClose}
        onSignupAndBook={handleNewsletterSignup}
        onSkipAndBook={handleSkipNewsletter}
        camp={camp}
      />
    </div>
  );
};

export default CampContent;
